import { createModel } from "@rematch/core";
import { fetchFrom } from '../utils/fetchData'
import { getFilteredCartItems, getTotalPrice } from "../utils/cartHelper";
import { DELIVERY_TYPE, type DeliveryType } from '../utils/deliveryHelper';

interface StateT {
  deliveryType: DeliveryType,
  items: Array<ItemT>,
  cartId: string | null,
  totalPrice: number,
  deliveryFee: number,
  tableNumber: string | undefined,
  couponLoading?: boolean,
  couponApplied?: boolean,
  coupon?: Coupon,
  deliveryItemDetails?: {
    deliveryProviderId: string,
    deliveryProviderType: number,
  }
}

export type RawItemT = {
  id: string,
  displayName: string,
  unitPrice: number,
  customizationMenuItems: Array<any>,
  quantity: number,
  totalPrice: number,
  itemType: number
}

export type ItemT = {
  id: string,
  name: string,
  price: number,
  totalPrice: number,
  quantity: number,
  customizationMenuItems: Array<any>,
}

export type Coupon = {
  campaignId: string
  discount: number
  campaignType?: number
  valid: boolean | null
  reasons?: Array<any>
  totalDiscount?: number
  code?: string
}

export type CouponValidation = {
  valid?: boolean,
  reasons?: {
    reasonCode: string
  }[]
}

export const initialCartState: StateT = {
  cartId: null,
  deliveryType: 'IDLE',
  items: [],
  totalPrice: 0,
  deliveryFee: 0,
  tableNumber: undefined,
  couponLoading: false,
  couponApplied: false,
  coupon: {
    campaignId: '',
    discount: 0,
    campaignType: 0,
    valid: null,
    reasons: [],
    totalDiscount: 0,
    code: ''
  },
  deliveryItemDetails: undefined
}

export const cart = createModel()({
  state: initialCartState,
  reducers: {
    setCartId (state, payload: string) {
      return { ...state, cartId: payload };
    },
    setDeliveryType (state, payload: DeliveryType) {
      return { ...state, deliveryType: payload };
    },
    setCartItems (state, payload: Array<ItemT>) {
      return { ...state, items: payload };
    },
    setTotalPrice (state, payload: number) {
      return { ...state, totalPrice: payload };
    },
    setDeliveryFee (state, payload: number) {
      return { ...state, deliveryFee: payload };
    },
    setTableNumber (state, payload: string) {
      return { ...state, tableNumber: payload };
    },
    setCouponLoading (state, payload: boolean) {
      return { ...state, couponLoading: payload }
    },
    setCouponApplied (state, payload: boolean) {
      return { ...state, couponApplied: payload }
    },
    setCoupon (state, payload: Coupon) {
      return { ...state, coupon: payload }
    },
    setDeliveryItemDetails (state, payload: { deliveryProviderId: string, deliveryProviderType: number } | undefined) {
      return { ...state, deliveryItemDetails: payload }
    },
  },
  effects: (dispatch) => ({
    selectDeliveryType (payload: { type: DeliveryType, deliveryProviderId?: string, deliveryProviderType?: number }) {
      const { type, deliveryProviderId, deliveryProviderType: providedDeliveryProviderType } = payload
      const deliveryProviderType = providedDeliveryProviderType || (type === DELIVERY_TYPE.TAKEAWAY ? 700 : 600);

      dispatch.cart.setDeliveryType(payload.type)
      dispatch.cart.setDeliveryItemDetails({
        deliveryProviderId: deliveryProviderId || 'deliveryId',
        deliveryProviderType: deliveryProviderType,
      })
    },
    async fetchCart (payload, state) {
      const storedCartId = localStorage.getItem('cartId') !== null ? JSON.parse(localStorage.getItem('cartId') || '""') : null
      const { cartId } = state?.cart
      if (cartId || storedCartId) {
        try {
          const res = await fetchFrom(`/cart/${cartId ? cartId : storedCartId}`)
          const { items, tableNumber, totalPrice } = res
          if (items) {
            dispatch.cart.setDeliveryType(items.find(item => item.categoryName === 'Leverans')?.itemType === 600 ? DELIVERY_TYPE.EATIN : DELIVERY_TYPE.TAKEAWAY)
            const cartItems = getFilteredCartItems(items as RawItemT[])
            const total = totalPrice ? totalPrice / 100 : getTotalPrice(cartItems)
            
            const deliveryItem = items.find((item: RawItemT) => [400, 500, 600, 700].includes(item.itemType))
            const deliveryFee = deliveryItem ? deliveryItem.unitPrice / 100 : 0

            dispatch.cart.setTableNumber(tableNumber)
            dispatch.cart.setCartItems(cartItems)
            dispatch.cart.setTotalPrice(total)
            dispatch.cart.setDeliveryFee(deliveryFee)

            storedCartId && !cartId && dispatch.cart.setCartId(storedCartId)
          }
        } catch (error) {
          console.log(error)
        }
      }
    },
    async addItemToCart (payload, state) {
      const { productId, storeId, itemType = 100, customizationItems, amount = 1 } = payload
      const { cartId } = state.cart

      let currentCartId: any = cartId
      if (!cartId) {
        currentCartId = await dispatch.cart.createCart(storeId)
      }

      try {
        const response = await fetchFrom(`/v2/cart/${currentCartId}/add`, {
          method: 'POST',
          body: [{
            id: productId,
            itemType,
            customizationItems,
            amount,
          }]
        })
        if (response.items) {
          const cartItems = getFilteredCartItems(response.items as RawItemT[])
          const total = response.totalPrice ? response.totalPrice / 100 : getTotalPrice(cartItems)
        
          dispatch.cart.setCartItems(cartItems)
          dispatch.cart.setTotalPrice(total)
        }
      } catch (error) {
        console.log(error)
      }
    },
    async addItemsToCart ({ itemsToAdd, storeId }, state) {

      if (!itemsToAdd || itemsToAdd.length < 1) {
        return;
      }

      const { cartId } = state.cart

      let currentCartId: any = cartId
      if (!cartId) {
        currentCartId = await dispatch.cart.createCart(storeId)
      }
      try {
        const response = await fetchFrom(`/v2/cart/${currentCartId}/add`, {
          method: 'POST',
          body: itemsToAdd.map((item: any) => {
            return {
              id: item.productId,
              itemType: item.itemType,
              customizationItems: item.customizationItems,
              amount: item.amount,
            }
          })
        })

        if (response.items && response.items.length > 0) {
          const cartItems = getFilteredCartItems(response.items as RawItemT[])
          const total = response.totalPrice ? response.totalPrice / 100 : getTotalPrice(cartItems)

          dispatch.cart.setCartItems(cartItems)
          dispatch.cart.setTotalPrice(total)

          return true
        }
        return false
      } catch (error) {
        console.log(error)
        return false
      }
    },
    async removeItemFromCart (cartItemId, state) {

      const { cartId }: { cartId: string | null } = state.cart || ''

      try {
        const response = await fetchFrom(`/v2/cart/${cartId}/${cartItemId}`, {
          method: 'DELETE'
        })
        if (response.items) {
          const cartItems = getFilteredCartItems(response.items as RawItemT[])
          const total = response.totalPrice ? response.totalPrice / 100 : getTotalPrice(cartItems)
          
          dispatch.cart.setCartItems(cartItems)
          dispatch.cart.setTotalPrice(total)
        }
      } catch (error) {
        console.log(error)
      }
    },
    async createCart (storeId, state) {
      const { tableNumber, deliveryType, deliveryItemDetails } = state.cart

      try {
        const { id } = await fetchFrom('/v2/cart', {
          method: 'POST',
          body: {
            storeId,
            tableNumber
          }
        })

        if (id) {
          localStorage.setItem('cartId', JSON.stringify(id))
          dispatch.cart.setCartId(id)
          
          if (deliveryType && deliveryType !== 'IDLE' && deliveryItemDetails) {
            const { deliveryProviderId, deliveryProviderType } = deliveryItemDetails as { deliveryProviderId: string, deliveryProviderType: number }
            try {
              const response = await fetchFrom(`/v2/cart/${id}/add`, {
                method: 'POST',
                body: [{
                  id: deliveryProviderId,
                  itemType: deliveryProviderType,
                  amount: 1,
                }]
              })
              
              if (response.items) {
                const deliveryItem = response.items.find((item: RawItemT) => [400, 500, 600, 700].includes(item.itemType))
                const deliveryFee = deliveryItem ? deliveryItem.unitPrice / 100 : 0
                dispatch.cart.setDeliveryFee(deliveryFee)
              }
            } catch (error) {
              console.log('Error adding delivery item to cart:', error)
            }
          }
          
          return id
        }
      } catch (error) {
        console.log(error)
      }
    },
    async updateItemQuantity ({ cartItemId, storeId, quantity }, state) {
      const { cartId } = state.cart
      let currentCartId: any = cartId
      if (!cartId) {
        currentCartId = await dispatch.cart.createCart(storeId)
      }
      try {
        const response = await fetchFrom(`/v2/cart/${currentCartId}/${cartItemId}`, {
          method: 'PATCH',
          body: {
            quantity
          }
        })
        if (response.items) {
          const cartItems = getFilteredCartItems(response.items as RawItemT[])
          const total = response.totalPrice ? response.totalPrice / 100 : getTotalPrice(cartItems)
          
          dispatch.cart.setCartItems(cartItems)
          dispatch.cart.setTotalPrice(total)
        }
      } catch (error) {
        console.log(error)
      }
    },
    async validateCoupon (couponCode: string, state) {
      const { cartId, coupon: { code }, couponApplied } = state.cart
      dispatch.cart.setCouponLoading(true)
      try {
        const discount = await fetchFrom(`/cart/${cartId}/discountcode/${couponCode}/validate`, {
          method: 'PUT',
        })

        if (discount.valid && couponCode === code && discount.campaignType === 10) {
          dispatch.cart.setCouponLoading(false)
          return { valid: true, reasons: discount.reasons }
        } else if (discount.valid) {
          const coupon: Coupon = { ...discount, code: couponCode, campaignType: discount.campaignType }
          const applyResult = await dispatch.cart.applyCoupon(coupon, state)
          return applyResult || { valid: true, reasons: discount.reasons }
        } else {
          dispatch.cart.removeCoupon(cartId)
          dispatch.cart.setCoupon({})
          dispatch.cart.setCouponApplied(false)
          dispatch.cart.setCouponLoading(false)
          return { valid: false, reasons: discount.reasons }
        }
      }
      catch (error) {
        console.log('Could not validate coupon', error as Error)
      }
    },
    async applyCoupon (coupon: Coupon, state) {
      const { cartId } = state.cart
      const { code } = coupon

      try {
        const data = await fetchFrom(`/cart/${cartId}/adddiscountcode`, {
          method: 'PUT',
          body: code,
        })

        if (data.success) {
          const { items, totalPrice, discount } = data.cart
          
          if (!totalPrice || totalPrice <= 0) {
            await dispatch.cart.removeCoupon(cartId)
            return { valid: false, reasons: [{ reasonCode: 'DISCOUNT_EXCEEDS_TOTAL' }] }
          }
          
          const appliedDiscount: Coupon = { ...coupon, code: coupon.code, totalDiscount: discount.totalDiscount / 100, campaignType: coupon.campaignType }
          if (items && totalPrice) {
            if (coupon.campaignType !== 10) {
              const cartItems = getFilteredCartItems(items as RawItemT[])
              const total = getTotalPrice(cartItems)
              dispatch.cart.setCartItems(cartItems)
              dispatch.cart.setTotalPrice(total)
            } else {
              const cartItems = getFilteredCartItems(items as RawItemT[])
              dispatch.cart.setCartItems(cartItems)
              dispatch.cart.setTotalPrice(totalPrice / 100)
            }
            dispatch.cart.setCouponApplied(true)
            dispatch.cart.setCoupon(appliedDiscount)
            dispatch.cart.setCouponLoading(false)
          }
        } else {
          dispatch.cart.setCoupon({})
          dispatch.cart.setCouponApplied(false)
          dispatch.cart.setCouponLoading(false)
          return false
        }
      } catch (error) {
        console.log('Could not apply coupon', error as Error)
        return false
      }
    },
    async removeCoupon (cartId: string) {
      try {
        const res = await fetchFrom(`/cart/${cartId}/removediscount`, {
          method: 'PUT',
          body: {
            cartId: cartId
          }
        })
        if (res) {
          const cartItems = getFilteredCartItems(res.items as RawItemT[])
          const total = getTotalPrice(cartItems)
          dispatch.cart.setCartItems(cartItems)
          dispatch.cart.setTotalPrice(total)
          dispatch.cart.setCoupon({})
          dispatch.cart.setCouponApplied(false)
          dispatch.cart.setCouponLoading(false)
        }
      } catch (error) {
        console.log('Could not remove coupon', error as Error)
      }
    }
  }),
});
